import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { Article } from "@/data/articles";
import { articleImageAlts } from "@/data/articleImages";

type Props = {
  articles: Article[];
  pageSize?: number;
};

export function ArticleCarousel({ articles, pageSize = 4 }: Props) {
  const [page, setPage] = useState(0);
  const totalPages = Math.max(1, Math.ceil(articles.length / pageSize));
  const start = page * pageSize;
  const visible = articles.slice(start, start + pageSize);

  const next = () => setPage((p) => (p + 1) % totalPages);
  const prev = () => setPage((p) => (p - 1 + totalPages) % totalPages);

  return (
    <div className="relative">
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-6">
        {visible.map((a) => (
          <Link
            key={a.slug}
            to="/clanek/$slug"
            params={{ slug: a.slug }}
            className="group block border border-border rounded-lg overflow-hidden hover:border-accent transition-colors bg-card"
          >
            {a.featuredImage && (
              <div className="aspect-[16/10] overflow-hidden bg-muted">
                <img
                  src={a.featuredImage}
                  alt={articleImageAlts[a.slug] ?? a.title}
                  title={a.title}
                  width={640}
                  height={400}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                />
              </div>
            )}
            <div className="p-3 md:p-4">
              <h3 className="font-serif text-sm md:text-lg leading-snug text-foreground group-hover:text-accent min-h-[2.5rem] md:min-h-[2.8rem] line-clamp-2">
                {a.title}
              </h3>
              <p className="mt-2 text-xs text-muted-foreground line-clamp-2">{a.excerpt}</p>
            </div>
          </Link>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={prev}
            aria-label="Prejšnji članki"
            className="h-10 w-10 rounded-full border border-border bg-background hover:border-accent hover:text-accent transition-colors flex items-center justify-center"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <span className="text-xs text-muted-foreground tabular-nums">
            {page + 1} / {totalPages}
          </span>
          <button
            type="button"
            onClick={next}
            aria-label="Naslednji članki"
            className="h-10 w-10 rounded-full border border-border bg-background hover:border-accent hover:text-accent transition-colors flex items-center justify-center"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      )}
    </div>
  );
}
